// Three-column no-code form builder for a request type. Layout:
//
//   ┌───────────────────────────────────────────────────────┐
//   │  PALETTE  │     EDITOR (draggable list)    │  PREVIEW │
//   └───────────────────────────────────────────────────────┘
//
//  Palette  — click a field type to append it to the editor.
//  Editor   — draggable rows of fields. Each row expands into a config
//             pane with label, required, help, placeholder, options.
//  Preview  — live render using the same PortalField used on the
//             customer-facing portal, so what admins see is what
//             customers fill in.
//
// State is local; saves are explicit via the "Save form" button which
// PUTs the whole schema to /service-desk/request-types/:id/form-schema.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Plus, Trash, DotsSixVertical, CaretDown, CaretRight, ArrowLeft,
  FloppyDisk, X, Eye,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { FIELD_PALETTE, FIELD_BY_TYPE, defaultFieldFor } from '../../components/serviceDesk/formBuilder/fieldTypes';
import PortalField from '../../components/serviceDesk/formBuilder/PortalField';
import { isFieldVisible } from '../../components/serviceDesk/formBuilder/formConditions';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';

function deriveKey(label, existing) {
  const base = (label || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40) || 'field';
  let key = base; let i = 2;
  while (existing.has(key)) { key = `${base}_${i++}`; }
  return key;
}

export default function FormBuilderPage() {
  const { rtId, portalId } = useParams();
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [rt, setRt] = useState(null);
  const [fields, setFields] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [previewState, setPreviewState] = useState({}); // form_data preview
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);

  const sdEnabled = caps?.service_desk_enabled === true;
  const isAdminRole = ['owner', 'admin'].includes(currentOrg?.role);

  useEffect(() => {
    if (!currentOrg || !sdEnabled) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/service-desk/portals/${portalId}/request-types`,
          { withCredentials: true, headers: getOrgHeaders() });
        const found = (data || []).find(x => x.request_type_id === rtId);
        setRt(found);
        setFields(Array.isArray(found?.form_fields) ? found.form_fields : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [currentOrg, sdEnabled, portalId, rtId, API, getOrgHeaders]);

  if (!sdEnabled) return <ServiceDeskUpsell />;
  if (!isAdminRole) return <div className="flex-1 flex items-center justify-center bg-zinc-50"><p className="text-sm text-zinc-500">Admins only.</p></div>;
  if (loading) return <div className="flex-1 flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!rt) return <div className="flex-1 flex items-center justify-center text-zinc-400">Request type not found</div>;

  const addField = (type) => {
    setFields(prev => {
      const taken = new Set(prev.map(f => f.key));
      const d = defaultFieldFor(type);
      d.label = FIELD_BY_TYPE[type]?.label || 'New field';
      d.key = deriveKey(d.label, taken);
      const next = [...prev, d];
      setExpandedId(d.id);
      return next;
    });
  };

  const updateField = (id, patch) => {
    setFields(prev => prev.map(f => {
      if (f.id !== id) return f;
      const merged = { ...f, ...patch };
      // Re-derive key when label changes UNLESS the key was hand-edited
      // in this session (we never re-derive once admin touches key).
      if ('label' in patch && !f._key_locked) {
        const taken = new Set(prev.filter(x => x.id !== id).map(x => x.key));
        merged.key = deriveKey(patch.label, taken);
      }
      return merged;
    }));
  };

  const removeField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    setFields(prev => {
      const next = [...prev];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/service-desk/request-types/${rtId}/form-schema`,
        { form_fields: fields },
        { withCredentials: true, headers: getOrgHeaders() });
      toast.success('Form saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex-1 overflow-hidden bg-zinc-50 flex flex-col" data-testid="sd-form-builder">
      <div className="border-b border-zinc-200 bg-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/service-desk/settings/portal/${portalId}`)}
            className="text-zinc-500 hover:text-zinc-900" data-testid="sd-fb-back">
            <ArrowLeft size={16} />
          </button>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-blue-600 font-semibold">Form builder</p>
            <h1 className="text-base font-bold text-zinc-900 font-['Outfit']">{rt.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowPreview(s => !s)}>
            <Eye size={13} className="mr-1.5" /> {showPreview ? 'Hide' : 'Show'} preview
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="sd-fb-save">
            <FloppyDisk size={13} className="mr-1.5" weight="fill" /> {saving ? 'Saving…' : 'Save form'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* PALETTE */}
        <aside className="w-56 border-r border-zinc-200 bg-white overflow-auto" data-testid="sd-fb-palette">
          <div className="p-3 border-b border-zinc-100">
            <p className="text-[10.5px] uppercase tracking-wider font-bold text-zinc-500">Add field</p>
          </div>
          <div className="p-2 space-y-1">
            {FIELD_PALETTE.map(({ type, label, Icon, description }) => (
              <button key={type} onClick={() => addField(type)}
                className="w-full text-left px-2.5 py-2 rounded-md hover:bg-zinc-50 flex items-start gap-2 group"
                data-testid={`sd-fb-add-${type}`}>
                <Icon size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900">{label}</p>
                  <p className="text-[10.5px] text-zinc-500 leading-tight">{description}</p>
                </div>
                <Plus size={12} className="text-zinc-300 group-hover:text-zinc-700 mt-1 flex-shrink-0" />
              </button>
            ))}
          </div>
        </aside>

        {/* EDITOR */}
        <main className="flex-1 overflow-auto p-6" data-testid="sd-fb-editor">
          <div className="max-w-2xl mx-auto">
            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
              These fields are the entire customer-facing form for <strong>{rt.name}</strong>. The ticket summary is derived from the first text field (or a field named Summary); add a Summary field to control it explicitly.
            </div>

            {fields.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-zinc-300 rounded-lg p-12 text-center">
                <p className="text-sm text-zinc-500">No custom fields yet.</p>
                <p className="text-xs text-zinc-400 mt-1">Pick a field type from the left to get started.</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="fields">
                  {(prov) => (
                    <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-2">
                      {fields.map((f, idx) => (
                        <Draggable key={f.id} draggableId={f.id} index={idx}>
                          {(p, snap) => (
                            <div ref={p.innerRef} {...p.draggableProps}
                              className={`bg-white border rounded-lg ${snap.isDragging ? 'border-blue-400 shadow-lg' : 'border-zinc-200'}`}
                              data-testid={`sd-fb-field-${f.id}`}>
                              <div className="flex items-center gap-2 px-3 py-2">
                                <span {...p.dragHandleProps} className="text-zinc-300 hover:text-zinc-600 cursor-grab active:cursor-grabbing"
                                  data-testid={`sd-fb-handle-${f.id}`}>
                                  <DotsSixVertical size={16} />
                                </span>
                                <button onClick={() => setExpandedId(expandedId === f.id ? null : f.id)}
                                  className="flex-1 flex items-center gap-2 text-left">
                                  {expandedId === f.id ? <CaretDown size={12} className="text-zinc-400" /> : <CaretRight size={12} className="text-zinc-400" />}
                                  <span className="text-sm font-medium text-zinc-900">{f.label || '(no label)'}</span>
                                  <span className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-600 rounded px-1.5 py-0.5">{f.type}</span>
                                  {f.required && <span className="text-[10px] bg-red-50 text-red-700 ring-1 ring-red-200 rounded px-1.5 py-0.5">Required</span>}
                                </button>
                                <button onClick={() => removeField(f.id)} className="text-zinc-400 hover:text-red-600 p-1"
                                  data-testid={`sd-fb-remove-${f.id}`}>
                                  <Trash size={13} />
                                </button>
                              </div>
                              {expandedId === f.id && (
                                <FieldConfigPanel field={f} fields={fields}
                                  onUpdate={(patch) => updateField(f.id, patch)} />
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {prov.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </main>

        {/* PREVIEW */}
        {showPreview && (
          <aside className="w-96 border-l border-zinc-200 bg-white overflow-auto" data-testid="sd-fb-preview">
            <div className="p-3 border-b border-zinc-100 flex items-center justify-between">
              <p className="text-[10.5px] uppercase tracking-wider font-bold text-zinc-500">Preview</p>
              <button onClick={() => setShowPreview(false)} className="text-zinc-400 hover:text-zinc-900"><X size={13} /></button>
            </div>
            <div className="p-4">
              <h2 className="text-base font-bold text-zinc-900 mb-3 font-['Outfit']">{rt.name}</h2>
              <div className="space-y-3">
                {fields.length === 0 && <p className="text-xs text-zinc-400 italic">Add fields on the left to see them rendered here.</p>}
                {fields.filter(f => isFieldVisible(f, previewState)).map(f => (
                  <PortalField key={f.id} field={f}
                    value={previewState[f.key]}
                    onChange={(v) => setPreviewState(s => ({ ...s, [f.key]: v }))} />
                ))}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

// ─── Field config panel ──────────────────────────────────
function FieldConfigPanel({ field, fields, onUpdate }) {
  const hasOptions = ['select', 'multiselect', 'radio', 'priority'].includes(field.type);
  const addOption = () => {
    const n = (field.options || []).length + 1;
    onUpdate({ options: [...(field.options || []), { label: `Option ${n}`, value: `option_${n}` }] });
  };
  const updateOption = (i, patch) => {
    const next = [...(field.options || [])];
    next[i] = { ...next[i], ...patch };
    onUpdate({ options: next });
  };
  const removeOption = (i) => {
    const next = [...(field.options || [])];
    next.splice(i, 1);
    onUpdate({ options: next });
  };

  return (
    <div className="border-t border-zinc-100 p-3 bg-zinc-50 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px]">Label</Label>
          <Input value={field.label} onChange={(e) => onUpdate({ label: e.target.value })}
            data-testid={`sd-fb-cfg-label-${field.id}`} />
        </div>
        <div>
          <Label className="text-[11px]">Field key</Label>
          <Input value={field.key} onChange={(e) => onUpdate({ key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''), _key_locked: true })}
            className="font-mono text-xs" data-testid={`sd-fb-cfg-key-${field.id}`} />
        </div>
      </div>

      <div>
        <Label className="text-[11px]">Help text</Label>
        <Input value={field.help_text || ''} onChange={(e) => onUpdate({ help_text: e.target.value })}
          placeholder="Optional hint shown below the field" />
      </div>

      {!['checkbox', 'date'].includes(field.type) && (
        <div>
          <Label className="text-[11px]">Placeholder</Label>
          <Input value={field.placeholder || ''} onChange={(e) => onUpdate({ placeholder: e.target.value })} />
        </div>
      )}

      {hasOptions && (
        <div>
          <Label className="text-[11px]">Options</Label>
          <div className="space-y-1.5">
            {(field.options || []).map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={o.label} onChange={(e) => updateOption(i, { label: e.target.value, value: e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, '_') })}
                  className="text-xs" placeholder={`Option ${i + 1}`}
                  data-testid={`sd-fb-cfg-option-${field.id}-${i}`} />
                <button onClick={() => removeOption(i)} className="text-zinc-400 hover:text-red-600 p-1"><X size={12} /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addOption} className="text-xs h-7" data-testid={`sd-fb-cfg-add-option-${field.id}`}>
              <Plus size={11} className="mr-1" /> Add option
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <Switch checked={!!field.required} onCheckedChange={(v) => onUpdate({ required: v })}
            data-testid={`sd-fb-cfg-required-${field.id}`} />
          <span>Required</span>
        </label>
      </div>

      <ConditionalVisibility field={field} fields={fields} onUpdate={onUpdate} />
    </div>
  );
}

// ─── Conditional visibility config ──────────────────────────────────
function ConditionalVisibility({ field, fields, onUpdate }) {
  const cond = field.visible_when || {};
  const otherFields = fields.filter(x => x.id !== field.id);
  const controlling = fields.find(x => x.key === cond.field);
  const hasOptions = controlling && ['select', 'multiselect', 'radio', 'priority'].includes(controlling.type);

  return (
    <div className="pt-3 border-t border-zinc-200">
      <Label className="text-[11px]">Conditional visibility</Label>
      <p className="text-[10.5px] text-zinc-500 mb-1.5">Show this field only when another field matches a value.</p>
      <div className="grid grid-cols-3 gap-2">
        <Select
          value={cond.field || '__none'}
          onValueChange={(v) => onUpdate({ visible_when: v === '__none' ? null : { field: v, operator: 'equals', value: '' } })}
        >
          <SelectTrigger className="text-xs" data-testid={`sd-fb-cfg-cond-field-${field.id}`}>
            <SelectValue placeholder="Always show" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">Always show</SelectItem>
            {otherFields.map(o => <SelectItem key={o.id} value={o.key}>{o.label || o.key}</SelectItem>)}
          </SelectContent>
        </Select>

        {cond.field && (
          <>
            <Select value={cond.operator || 'equals'}
              onValueChange={(v) => onUpdate({ visible_when: { ...cond, operator: v } })}>
              <SelectTrigger className="text-xs" data-testid={`sd-fb-cfg-cond-op-${field.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="equals">equals</SelectItem>
                <SelectItem value="not_equals">not equals</SelectItem>
              </SelectContent>
            </Select>

            {hasOptions ? (
              <Select value={cond.value || ''} onValueChange={(v) => onUpdate({ visible_when: { ...cond, value: v } })}>
                <SelectTrigger className="text-xs" data-testid={`sd-fb-cfg-cond-value-${field.id}`}><SelectValue placeholder="value" /></SelectTrigger>
                <SelectContent>
                  {(controlling.options || []).map(o => <SelectItem key={o.value} value={o.value}>{o.label || o.value}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : controlling && controlling.type === 'checkbox' ? (
              <Select value={cond.value || 'true'} onValueChange={(v) => onUpdate({ visible_when: { ...cond, value: v } })}>
                <SelectTrigger className="text-xs" data-testid={`sd-fb-cfg-cond-value-${field.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">checked</SelectItem>
                  <SelectItem value="false">unchecked</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input value={cond.value || ''} onChange={(e) => onUpdate({ visible_when: { ...cond, value: e.target.value } })}
                className="text-xs" placeholder="value" data-testid={`sd-fb-cfg-cond-value-${field.id}`} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
